// codAmount — the single source of truth for how much CASH a courier collects
// on delivery. Every courier booking MUST use this; collecting the wrong
// amount is real money lost.
//
// Partial COD: an advance (e.g. ₹99) is collected prepaid via a payment link,
// so the courier collects only the REMAINDER. Full COD: advance = 0, courier
// collects the whole total. Prepaid: 0.

const money = (v) => {
    const n = Math.round(Number(v));
    return Number.isFinite(n) ? n : 0;
};

/** True when the order is any kind of COD (full or partial). */
export const isCodOrder = (order) => {
    const p = order?.payment || {};
    const mode = String(p.mode || p.method || '').toUpperCase();
    return mode.includes('COD');
};

/** The advance already collected up front on a COD order (0 for full COD). */
export const codAdvance = (order) => {
    if (!isCodOrder(order)) return 0;
    return Math.max(0, money(order?.payment?.codAdvance));
};

/** Partial COD = a COD order that carries a non-zero prepaid advance. */
export const isPartialCod = (order) => isCodOrder(order) && codAdvance(order) > 0;

/**
 * The exact cash the courier must collect on delivery.
 *  - Prepaid           → 0
 *  - Full COD          → payment total
 *  - Partial COD       → payment total − advance (never below 0)
 */
export const codCollectable = (order) => {
    if (!isCodOrder(order)) return 0;
    const total = money(order?.payment?.total ?? order?.total ?? order?.amount);
    return Math.max(0, total - codAdvance(order));
};

/**
 * The words a human reads and acts on — printed on the courier's own label and
 * on ours.
 *
 * This exists because the NUMBER and the WORDS drifted apart. The booking sent
 * cod_amount = codCollectable() (correct), while the routing line that CEPT
 * prints on the parcel was built separately from payment.total. India Post's
 * record for DP07118 said ₹721 while the label on the box said "COD - COLLECT
 * RS.821 CASH" — the postman would have collected ₹100 the customer had
 * already paid.
 *
 * So the amount a person is told to collect now comes from the same function
 * as the amount the carrier is told to collect. Never format this figure by
 * hand again.
 */
export const codLabelText = (order) => (
    isCodOrder(order) ? `COD - COLLECT Rs.${codCollectable(order)} CASH` : 'PREPAID'
);

/**
 * Has the prepaid advance on a partial-COD order actually been collected?
 * Mirrors the prepaid-verified truth model: a verified PAID advance, or a
 * recorded codAdvancePaid flag/amount.
 */
export const isAdvancePaid = (order) => {
    if (!isPartialCod(order)) return true;              // nothing to collect
    const p = order?.payment || {};
    if (p.codAdvancePaid === true) return true;
    if (money(p.codAdvancePaidAmount) >= codAdvance(order)) return true;
    // A verified prepaid advance link counts (advance collected via Cashfree).
    if (String(p.advanceVerificationStatus || '').toUpperCase() === 'PAID') return true;
    return false;
};

/**
 * Can this order go to a courier? Partial COD must have its advance collected
 * first (business rule: ship only after the advance is paid).
 */
export const canShipCod = (order) => {
    if (!isPartialCod(order)) return true;
    return isAdvancePaid(order);
};
